import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { appErrorService } from '@/services/AppErrorService'

const SENSITIVE_KEYS = new Set([
  'Password',
  'ConfirmPassword',
  'NewPassword',
  'NewPasswordConfirmation',
  'SessionHandle'
])

function sanitizePayload(data: unknown): unknown {
  if (!data || typeof data !== 'object') return data
  return Object.fromEntries(
    Object.entries(data as Record<string, unknown>).map(([k, v]) => [
      k,
      SENSITIVE_KEYS.has(k) ? '[redacted]' : v
    ])
  )
}

interface Sunny16BaseServiceOptions {
  /**
   * When false, 4xx responses are still console-logged but NOT forwarded to
   * New Relic. Use for services whose 4xx responses represent expected user
   * errors (bad credentials, duplicate email, invalid reset code) rather
   * than booking app bugs. 5xx responses and network failures are always
   * reported. Defaults to true.
   */
  reportClientErrors?: boolean
}

class Sunny16BaseService {
  protected axiosInstance: AxiosInstance

  constructor(baseUrl: string, serviceLabel = 'API', options: Sunny16BaseServiceOptions = {}) {
    const reportClientErrors = options.reportClientErrors ?? true
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache'
      }
    })

    this.axiosInstance.interceptors.request.use((config) => {
      ;(config as any)._requestStart = Date.now()
      return config
    })

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (err) => {
        // Per-call opt-out: a request config may carry `_expectedError`, a
        // predicate returning true for response shapes the caller has chosen
        // to treat as benign (e.g. duplicate-tap DELETE → 400 "Cart Bundle
        // not found"). When matched, skip console + New Relic and let the
        // rejection propagate so the caller's own catch still runs.
        const expectedPredicate = (err.config as any)?._expectedError as
          | ((error: AxiosError) => boolean)
          | undefined
        if (typeof expectedPredicate === 'function' && expectedPredicate(err)) {
          return Promise.reject(err)
        }
        const start = (err.config as any)?._requestStart
        const duration = start ? `${Date.now() - start}ms` : '?ms'
        const method = err.config?.method?.toUpperCase() ?? 'REQUEST'
        const url = err.config?.url ?? ''
        const status = err.response?.status ?? 'ERR'
        console.error(`[${serviceLabel}] ← ${status} ${method} ${url} (${duration})`)
        let requestPayload: string | undefined
        if (err.config?.data) {
          try {
            const payload =
              typeof err.config.data === 'string' ? JSON.parse(err.config.data) : err.config.data
            const sanitized = sanitizePayload(payload)
            console.error(`[${serviceLabel}] payload:`, sanitized)
            requestPayload = JSON.stringify(sanitized)
          } catch {
            console.error(`[${serviceLabel}] payload: [unparseable]`)
            requestPayload = '[unparseable]'
          }
        }
        const isClientError =
          typeof err.response?.status === 'number' &&
          err.response.status >= 400 &&
          err.response.status < 500
        if (reportClientErrors || !isClientError) {
          appErrorService.reportAxiosError(err, {
            serviceLabel,
            method,
            url,
            status,
            duration,
            requestPayload
          })
        }
        return Promise.reject(err)
      }
    )
  }
}

export default Sunny16BaseService
